<script setup lang="ts">
import type { TextSettings, ThemeSettings, CommentSettings } from '~/stores/auth'
import type { CommentItem as CommentItemType } from '~/stores/comments'
import type { CharacterPageData, CharacterPageSettings } from '~/composables/useCharacterPage'

const props = defineProps<{
  character: CharacterPageData
  settings: CharacterPageSettings
  themeSettings: ThemeSettings
  textSettings: TextSettings
  commentSettings: CommentSettings
  editMode?: boolean
  isOwner: boolean
}>()

const emit = defineEmits<{
  'open-comment-settings': []
}>()

const authStore = useAuthStore()
const commentsStore = useCommentsStore()
const toast = useToast()
const { requirePlan } = usePlanGate()

const commentsUnlocked = computed(() => {
  if (props.editMode) return authStore.canAccessBoolean('comments')
  return props.character.plan === 'pro' || props.character.plan === 'elite'
})

const isLoggedIn = computed(() => !!authStore.userId)
const currentPage = ref(1)
const isFriend = ref(false)

const isFriendsOnly = computed(() =>
  props.commentSettings.allowComments === 'friends_only'
)

const skinPreview = inject<boolean>('skinPreview', false)

const visible = ref(false)
onMounted(async () => {
  visible.value = true
  if (skinPreview) {
    // Populate store with mock comments for preview
    commentsStore.comments = [
      { id: 'mc1', author: { id: 'a1', username: 'GamerGirl', display_name: null, avatar_url: null }, content: 'This page looks amazing! Love the setup.', created_at: new Date(Date.now() - 3600000).toISOString(), updated_at: new Date(Date.now() - 3600000).toISOString() },
      { id: 'mc2', author: { id: 'a2', username: 'ProPlayer', display_name: 'Pro Player', avatar_url: null }, content: 'Great taste in games! We should play sometime.', created_at: new Date(Date.now() - 7200000).toISOString(), updated_at: new Date(Date.now() - 7200000).toISOString() },
      { id: 'mc3', author: { id: 'a3', username: 'NightOwl', display_name: null, avatar_url: null }, content: 'Nice clips, that clutch was insane!', created_at: new Date(Date.now() - 86400000).toISOString(), updated_at: new Date(Date.now() - 86400000).toISOString() }
    ]
    commentsStore.total = 3
    return
  }
  if (commentsUnlocked.value) {
    commentsStore.fetchComments(props.character.id, 1)
  }
  // Check friendship if needed
  if (isFriendsOnly.value && isLoggedIn.value && !props.isOwner && authStore.userId) {
    try {
      const result = await $fetch<{ status: string }>(`/api/character/${authStore.userId}/friend/status`, {
        query: { target: props.character.id }
      })
      isFriend.value = result.status === 'accepted'
    } catch {
      isFriend.value = false
    }
  }
})

watch(currentPage, (page) => {
  if (commentsUnlocked.value) {
    commentsStore.fetchComments(props.character.id, page)
  }
})

function handleCommentPosted(comment: CommentItemType) {
  commentsStore.addComment(comment)
}

async function handleDelete(commentId: string) {
  try {
    await $fetch(`/api/character/${props.character.id}/comment/${commentId}`, { method: 'DELETE' })
    commentsStore.removeComment(commentId)
    toast.add({ title: 'Comment deleted', icon: 'i-lucide-check', color: 'success', duration: 3000 })
  } catch (err: any) {
    toast.add({ title: 'Failed to delete', description: err.data?.message || 'Something went wrong', icon: 'i-lucide-alert-circle', color: 'error', duration: 5000 })
  }
}

async function handleReport(commentId: string) {
  try {
    await $fetch(`/api/character/${props.character.id}/comment/${commentId}/report`, { method: 'POST' })
    toast.add({ title: 'Comment reported', description: 'Thank you for helping keep the community safe.', icon: 'i-lucide-check', color: 'success', duration: 4000 })
  } catch (err: any) {
    const message = err.data?.message || 'Something went wrong'
    const isAlreadyReported = message.toLowerCase().includes('already reported')
    toast.add({ title: isAlreadyReported ? 'Already reported' : 'Failed to report', description: isAlreadyReported ? 'You have already reported this comment.' : message, icon: 'i-lucide-alert-circle', color: 'error', duration: 5000 })
  }
}
</script>

<template>
  <!-- Public page: hidden for free users -->
  <template v-if="!editMode && !commentsUnlocked" />

  <!-- Edit mode: locked state -->
  <Transition name="comments-slide">
    <div
      v-if="editMode && !commentsUnlocked && visible"
      class="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 backdrop-blur-sm cursor-pointer opacity-50 grayscale"
      @click="requirePlan('Comments', 'pro')"
    >
      <UIcon
        name="i-lucide-message-circle"
        class="size-3.5 shrink-0 text-white/40"
      />
      <span class="text-xs font-bold tracking-wide text-white/40">
        Comments
      </span>
      <UBadge
        color="fuchsia"
        variant="solid"
        size="xs"
        class="gap-0.5"
      >
        <UIcon
          name="i-lucide-lock"
          class="size-2"
        />
        Pro
      </UBadge>
    </div>
  </Transition>

  <!-- Unlocked: transparent comments list -->
  <Transition name="comments-slide">
    <div
      v-if="commentsUnlocked && visible"
      class="w-72"
    >
      <!-- Header -->
      <div class="flex items-center gap-1.5 mb-3">
        <UIcon
          name="i-lucide-message-circle"
          class="size-3 text-white/30"
        />
        <span class="text-[11px] font-medium text-white/40 uppercase tracking-wider">
          Comments
        </span>
        <span
          v-if="commentsStore.total > 0"
          class="text-[10px] text-white/25"
        >
          {{ commentsStore.total }}
        </span>
        <UTooltip
          v-if="editMode"
          text="Comment settings"
        >
          <UButton
            icon="i-lucide-settings"
            color="neutral"
            variant="ghost"
            size="xs"
            class="text-white/25 hover:text-white/50 p-0.5!"
            @click="emit('open-comment-settings')"
          />
        </UTooltip>
      </div>

      <!-- Comment form -->
      <div
        v-if="isLoggedIn && !editMode"
        class="mb-7"
      >
        <CommentForm
          v-if="!isOwner && isFriendsOnly && !isFriend"
          :character-id="character.id"
          :primary-color="themeSettings.primaryColor"
          disabled
        />
        <CommentForm
          v-else
          :character-id="character.id"
          :primary-color="themeSettings.primaryColor"
          @comment-posted="handleCommentPosted"
        />
      </div>

      <!-- Loading -->
      <div
        v-if="commentsStore.isLoading && commentsStore.comments.length === 0"
        class="flex items-center justify-center py-6"
      >
        <UIcon
          name="i-lucide-loader-2"
          class="size-4 text-white/20 animate-spin"
        />
      </div>

      <!-- Comments list -->
      <div
        v-else-if="commentsStore.comments.length > 0"
        class="space-y-3"
      >
        <CommentItem
          v-for="comment in commentsStore.comments"
          :key="comment.id"
          :comment="comment"
          :theme-settings="themeSettings"
          :text-settings="textSettings"
          :is-owner="isOwner"
          :is-author="comment.author.id === authStore.userId"
          :is-logged-in="isLoggedIn"
          @delete="handleDelete"
          @report="handleReport"
        />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!commentsStore.isLoading"
        class="flex flex-col items-center gap-1.5 py-6"
      >
        <UIcon
          name="i-lucide-message-circle"
          class="size-5 text-white/10"
        />
        <p class="text-[11px] text-white/25 text-center">
          {{ editMode ? 'Comments will appear here' : 'No comments yet' }}
        </p>
      </div>

      <!-- Pagination -->
      <div
        v-if="commentsStore.totalPages > 1"
        class="flex justify-center mt-3"
      >
        <UPagination
          v-model:page="currentPage"
          :total="commentsStore.total"
          :items-per-page="commentsStore.PAGE_SIZE"
          :sibling-count="1"
          :show-edges="false"
          size="xs"
          color="neutral"
          variant="ghost"
          active-color="primary"
        />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.comments-slide-enter-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 1.4s;
}
.comments-slide-leave-active {
  transition: all 0.3s ease-in;
}
.comments-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.comments-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
